import { Component, OnInit } from '@angular/core';
import {
  AdminService,
  CloudProviderId,
  KNOWN_SERVICE_TYPES,
  humanizeServiceType,
  MonitoringRule,
  MonitoringRulePayload,
  MonitoringRuleServiceType,
  providerForServiceType,
  providerMeta,
  SchedulerConfig,
} from '../../core/services/admin.service';

interface EditableRule {
  enabled:                      boolean;
  evaluationWindowMinutes:      number;
  metricPeriodSeconds:          number;
  errorRateThresholdPercent:    number;
  minimumSampleCount:           number;
  throttleCountThreshold:       number;
  severityHighThresholdPercent: number;
  overrideEnabled:              boolean;
}

interface ServiceTypeSection {
  serviceType: MonitoringRuleServiceType;
  label:       string;
  override:    MonitoringRule | null;
  editing:     boolean;
  draft:       EditableRule | null;
  saving:      boolean;
  error:       string;
}

/** Service-type override sections grouped under their cloud provider. */
interface ServiceTypeGroup {
  provider: CloudProviderId;
  label:    string;               // 'AWS' / 'Azure'
  sections: ServiceTypeSection[]; // same section instances as `serviceTypes`, alphabetized
}

function ruleToEditable(rule: MonitoringRule, overrideEnabled = true): EditableRule {
  return {
    enabled:                      rule.enabled,
    evaluationWindowMinutes:      rule.evaluationWindowMinutes,
    metricPeriodSeconds:          rule.metricPeriodSeconds,
    errorRateThresholdPercent:    rule.errorRateThresholdPercent,
    minimumSampleCount:           rule.minimumSampleCount,
    throttleCountThreshold:       rule.throttleCountThreshold,
    severityHighThresholdPercent: rule.severityHighThresholdPercent,
    overrideEnabled,
  };
}

@Component({
  selector: 'app-monitoring-rules',
  templateUrl: './monitoring-rules.component.html',
})
export class MonitoringRulesComponent implements OnInit {
  loading = true;
  error = '';

  schedulerConfig: SchedulerConfig | null = null;
  schedulerEditing = false;
  schedulerDraft: number | null = null;
  schedulerSaving = false;
  schedulerError = '';

  readonly pollingFrequencyOptions = [
    { value: 5,  label: '5 minutes'  },
    { value: 10, label: '10 minutes' },
    { value: 15, label: '15 minutes' },
    { value: 30, label: '30 minutes' },
    { value: 60, label: '60 minutes' },
  ];

  globalRule: MonitoringRule | null = null;
  globalEditing = false;
  globalDraft: EditableRule | null = null;
  globalSaving = false;
  globalError = '';

  /**
   * Override sections, built at load time from a data-driven source of truth:
   * every currently-supported service type PLUS any service type that already
   * has a persisted override (even one this UI build didn't ship with). This is
   * populated in ngOnInit — see buildServiceTypeSections().
   */
  serviceTypes: ServiceTypeSection[] = [];

  /**
   * The same override sections as `serviceTypes`, grouped by cloud provider
   * (AWS first, then Azure) and alphabetized within each group. Built once at
   * load time from the SAME section instances, so editing state and change
   * detection stay stable (see buildServiceTypeGroups()).
   */
  serviceTypeGroups: ServiceTypeGroup[] = [];

  readonly metricPeriodOptions = [
    { value: 60,   label: '1 minute'  },
    { value: 300,  label: '5 minutes' },
    { value: 900,  label: '15 minutes' },
    { value: 3600, label: '1 hour'    },
  ];

  constructor(private readonly adminService: AdminService) {}

  ngOnInit(): void {
    this.adminService.getSchedulerConfig().subscribe({
      next:  cfg => { this.schedulerConfig = cfg; },
      error: () => { this.schedulerError = 'Could not load scheduler config.'; },
    });

    this.adminService.listMonitoringRules().subscribe({
      next: res => {
        this.globalRule   = res.rules.find(r => r.scope === 'GLOBAL') ?? null;
        this.serviceTypes = this.buildServiceTypeSections(res.rules);
        this.serviceTypeGroups = this.buildServiceTypeGroups(this.serviceTypes);
        this.loading = false;
      },
      error: () => {
        this.error = 'Failed to load monitoring rules.';
        this.loading = false;
      },
    });
  }

  /**
   * Builds one section per service type from a combined source of truth:
   * the currently-supported services (KNOWN_SERVICE_TYPES) UNION any service
   * type that already has a persisted SERVICE_TYPE override. Known types keep
   * their product order; extra/unknown persisted types are appended
   * alphabetically so newer data never disappears. Labels are humanized so an
   * unmapped type still renders a sensible name.
   */
  private buildServiceTypeSections(rules: MonitoringRule[]): ServiceTypeSection[] {
    const overrides = rules.filter(
      (r): r is MonitoringRule & { serviceType: string } => r.scope === 'SERVICE_TYPE' && !!r.serviceType,
    );

    const persistedTypes = overrides.map(o => o.serviceType);
    const extras = [...new Set(persistedTypes)]
      .filter(t => !KNOWN_SERVICE_TYPES.includes(t))
      .sort();
    const orderedTypes = [...KNOWN_SERVICE_TYPES, ...extras];

    return orderedTypes.map(serviceType => ({
      serviceType,
      label:    humanizeServiceType(serviceType),
      override: overrides.find(o => o.serviceType === serviceType) ?? null,
      editing:  false,
      draft:    null,
      saving:   false,
      error:    '',
    }));
  }

  /**
   * Groups the flat override sections by cloud provider (AWS first, then Azure)
   * and alphabetizes them within each group. Reuses the SAME ServiceTypeSection
   * instances as `serviceTypes` so per-row editing state and change detection
   * stay stable — this is a stable field built once, never a getter. Empty
   * groups (a provider with no service types) are omitted.
   */
  private buildServiceTypeGroups(sections: ServiceTypeSection[]): ServiceTypeGroup[] {
    const order: CloudProviderId[] = ['AWS', 'AZURE', 'GCP'];
    return order
      .map(provider => ({
        provider,
        label: providerMeta(provider).label,
        sections: sections
          .filter(s => providerForServiceType(s.serviceType) === provider)
          .sort((a, b) => a.label.localeCompare(b.label)),
      }))
      .filter(group => group.sections.length > 0);
  }

  // ── Scheduler config ─────────────────────────────────────────────────────────

  startSchedulerEdit(): void {
    this.schedulerDraft   = this.schedulerConfig?.frequencyMinutes ?? 30;
    this.schedulerEditing = true;
    this.schedulerError   = '';
  }

  cancelSchedulerEdit(): void {
    this.schedulerEditing = false;
    this.schedulerDraft   = null;
    this.schedulerError   = '';
  }

  saveSchedulerConfig(): void {
    if (this.schedulerDraft == null) return;
    this.schedulerSaving = true;
    this.schedulerError  = '';

    this.adminService.updateSchedulerConfig(this.schedulerDraft).subscribe({
      next: result => {
        this.schedulerConfig  = { frequencyMinutes: result.frequencyMinutes, expression: result.expression, state: this.schedulerConfig?.state ?? 'ENABLED' };
        this.schedulerEditing = false;
        this.schedulerDraft   = null;
        this.schedulerSaving  = false;
      },
      error: (err) => {
        this.schedulerError  = err?.error?.message ?? 'Failed to update polling frequency.';
        this.schedulerSaving = false;
      },
    });
  }

  // ── Global rule ──────────────────────────────────────────────────────────────

  startGlobalEdit(): void {
    if (!this.globalRule) return;
    this.globalDraft   = ruleToEditable(this.globalRule);
    this.globalEditing = true;
    this.globalError   = '';
  }

  cancelGlobalEdit(): void {
    this.globalEditing = false;
    this.globalDraft   = null;
    this.globalError   = '';
  }

  saveGlobal(): void {
    if (!this.globalDraft) return;
    this.globalSaving = true;
    this.globalError  = '';

    const payload = draftToPayload(this.globalDraft);
    this.adminService.updateGlobalMonitoringRule(payload).subscribe({
      next: updated => {
        this.globalRule    = updated;
        this.globalEditing = false;
        this.globalDraft   = null;
        this.globalSaving  = false;
      },
      error: (err) => {
        this.globalError  = err?.error?.message ?? 'Failed to save global rule.';
        this.globalSaving = false;
      },
    });
  }

  // ── Service-type overrides ────────────────────────────────────────────────────

  startOverrideEdit(section: ServiceTypeSection): void {
    const source = section.override ?? this.globalRule;
    if (!source) return;
    section.draft   = ruleToEditable(source, section.override?.overrideEnabled ?? false);
    section.editing = true;
    section.error   = '';
  }

  cancelOverrideEdit(section: ServiceTypeSection): void {
    section.editing = false;
    section.draft   = null;
    section.error   = '';
  }

  saveOverride(section: ServiceTypeSection): void {
    if (!section.draft) return;
    section.saving = true;
    section.error  = '';

    const payload = draftToPayload(section.draft);
    this.adminService.updateServiceTypeMonitoringRule(section.serviceType, payload).subscribe({
      next: updated => {
        section.override = updated;
        section.editing  = false;
        section.draft    = null;
        section.saving   = false;
      },
      error: (err) => {
        section.error  = err?.error?.message ?? `Failed to save ${section.label} override.`;
        section.saving = false;
      },
    });
  }

  effectiveValue(section: ServiceTypeSection, field: keyof EditableRule): unknown {
    if (section.override?.overrideEnabled) {
      return (section.override as unknown as Record<string, unknown>)[field as string];
    }
    return this.globalRule ? (this.globalRule as unknown as Record<string, unknown>)[field as string] : '—';
  }

  isUsingOverride(section: ServiceTypeSection): boolean {
    return !!section.override?.overrideEnabled;
  }
}

function draftToPayload(draft: EditableRule): MonitoringRulePayload {
  return {
    enabled:                      draft.enabled,
    evaluationWindowMinutes:      draft.evaluationWindowMinutes,
    metricPeriodSeconds:          draft.metricPeriodSeconds,
    errorRateThresholdPercent:    draft.errorRateThresholdPercent,
    minimumSampleCount:           draft.minimumSampleCount,
    throttleCountThreshold:       draft.throttleCountThreshold,
    severityHighThresholdPercent: draft.severityHighThresholdPercent,
    overrideEnabled:              draft.overrideEnabled,
  };
}
